import {
	ButtonBase,
	Grid,
	IconButton as IconBtn,
	InputBase,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@mui/material';
import { helperText, useMainCustomStyle } from '../assets/theme/Theme';
import { useEffect, useState } from 'react';

import AlertDialog from '../components/AlertDialog';
import {
	DeleteIcon,
	EditIcon,
	EyeIcon,
	HideEyeIcon,
} from '../assets/icons/Icons';
import { toast } from 'react-toastify';
import { getAccessToken, getElderHomeId, getUserId } from '../Utils/AuthUtil';
import UserControlPopup from '../components/UserControlPopup';
import {
	USER_ROLES,
	USER_ROLES_WITH_LABEL,
	USER_TYPES,
	WEB_ACCOUNT,
} from '../Utils/Enums';
import {
	createOtherUserAccount,
	deleteOtherUser,
	fetchOtherUsers,
	updateUser,
} from '../services/UserService';
import TextInput from '../components/TextInput';
import IconButton from '../components/IconButton';
import SideNavigationBar from '../components/SideNavigationBar';
import PopupDialog from '../components/PopupDialog';
import CustomTextInput from '../components/CustomTextInput';

const INITIAL_END_USER_STATE = {
	firstName: '',
	lastName: '',
	email: '',
	password: '',
	userType: '',
};

const INITIAL_USER_STATE = {
	id: '',
	name: '',
	userType: '',
	userAccountType: '',
};

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

const END_USER_ERROR_STATE = {
	firstName: INITIAL_ERROR_STATE,
	lastName: INITIAL_ERROR_STATE,
	email: INITIAL_ERROR_STATE,
	password: INITIAL_ERROR_STATE,
	userType: INITIAL_ERROR_STATE,
};

function AdminDashboard() {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const userId = getUserId();
	const homeId = getElderHomeId();
	const [userList, setUserList] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(8);
	const [page, setPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(INITIAL_USER_STATE);
	const [accountSelectionError, setAccountSelectionError] =
		useState(INITIAL_ERROR_STATE);
	const [newEndUser, setNewEndUser] = useState(INITIAL_END_USER_STATE);
	const [newEndUserError, setNewEndUserError] =
		useState(END_USER_ERROR_STATE);
	const [show, setShow] = useState(false);
	const [isUserModalOpen, setIsUserModalOpen] = useState(false);
	const [userAccountTypes, setUserAccountTypes] = useState([
		{ name: 'Staff' },
	]);

	useEffect(() => {
		getAllUsers();
	}, []);

	async function getAllUsers() {
		setIsLoading(true);
		const data = await fetchOtherUsers(homeId, token);
		const restructuredList = Object.keys(data.content)
			.map(key => {
				return data.content[key].map(item => {
					return {
						...item,
						account:
							key.charAt(0).toUpperCase() +
							key.slice(1).toLowerCase(),
					};
				});
			})
			.flat(1);

		setUserList(restructuredList);
		setIsLoading(false);
	}

	async function removeUser(id, role) {
		const route = USER_ROLES.staff === role ? 'staff' : '';
		const response = await deleteOtherUser(id, route, token);
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('User removed Successfully');
		} else {
			toast.error(response.message);
		}
	}

	async function updateUserDetails() {
		const userType = USER_TYPES.find(
			item => item.name === selectedUser.userType,
		);

		const body = {
			userAccountVersion: userType ? userType.key : '',
		};

		const response = await updateUser(selectedUser.id, token, body);
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('User updated Successfully');
			setIsModalOpen(false);
		} else {
			toast.success(response.message);
		}
	}

	function onOpenUserControls(id, name, userAccountType) {
		const user = USER_TYPES.find(item => item.key === userAccountType);
		const userType = user ? user.name : '';
		setSelectedUser({ id, name, userType, userAccountType });
		setIsModalOpen(true);
	}

	function onChangeUserType(key) {
		setSelectedUser(prevState => ({ ...prevState, userType: key }));
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setSelectedUser(INITIAL_USER_STATE);
	}

	function onCloseUserModal() {
		setIsUserModalOpen(false);
		setNewEndUser(INITIAL_END_USER_STATE);
		setNewEndUserError(END_USER_ERROR_STATE);
	}

	function getUserAccountName(label) {
		if (!label) {
			return 'N/A';
		}

		const type = USER_TYPES.find(item => item.key === label);
		return type ? type.name : 'N/A';
	}

	function onHandleInputChange(value, name) {
		setNewEndUser(prevState =>
			prevState ? { ...prevState, [name]: value } : prevState,
		);

		setNewEndUserError(prevState =>
			prevState
				? { ...prevState, [name]: INITIAL_ERROR_STATE }
				: prevState,
		);
	}

	function validateInputs() {
		let isValid = true;

		if (!newEndUser.userType || newEndUser.userType === '') {
			setNewEndUserError(prevState => ({
				...prevState,
				userType: {
					status: true,
					msg: 'Select user type',
				},
			}));
			isValid = false;
		}

		if (!newEndUser.firstName || newEndUser.firstName === '') {
			setNewEndUserError(prevState => ({
				...prevState,
				firstName: {
					status: true,
					msg: 'Enter first name',
				},
			}));
			isValid = false;
		}

		if (!newEndUser.lastName || newEndUser.lastName === '') {
			setNewEndUserError(prevState => ({
				...prevState,
				lastName: {
					status: true,
					msg: 'Enter last name',
				},
			}));
			isValid = false;
		}

		if (!newEndUser.email || newEndUser.email === '') {
			setNewEndUserError(prevState => ({
				...prevState,
				email: {
					status: true,
					msg: 'Enter email',
				},
			}));
			isValid = false;
		}

		if (!newEndUser.password || newEndUser.password === '') {
			setNewEndUserError(prevState => ({
				...prevState,
				password: {
					status: true,
					msg: 'Enter Password',
				},
			}));
			isValid = false;
		}

		return isValid;
	}

	async function onAddNewEndUser() {
		const isInputsValid = validateInputs();
		if (!isInputsValid) {
			return;
		}

		const body = {
			firstname: newEndUser.firstName,
			lastname: newEndUser.lastName,
			username: `${newEndUser.firstName} ${newEndUser.lastName}`,
			email: newEndUser.email,
			password: newEndUser.password,
			accountType: WEB_ACCOUNT,
			elderHomeId: homeId,
			adminId: userId,
		};

		//Update this if new user roles added
		const userRoute = newEndUser.userType === 'Staff' ? 'staffSignUp' : '';

		const response = await createOtherUserAccount(body, userRoute);
		if (response.message === 'Success') {
			getAllUsers();
			toast.success('New User Added Successfully');
			onCloseUserModal();
		} else {
			toast.error(response.message);
		}
	}

	function getUserName(label) {
		if (!label) {
			return 'N/A';
		}

		const type = USER_TYPES.find(item => item.key === label);
		return type.name;
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid container paddingLeft="30px">
					<Grid item paddingLeft="30px">
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Users
						</Typography>
					</Grid>
					<Grid
						container
						width={'100%'}
						paddingRight="20px"
						justifyContent={'flex-end'}>
						<IconButton
							text={'Add New User'}
							onClick={() => setIsUserModalOpen(true)}
						/>
					</Grid>
				</Grid>
				<Grid container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid container paddingLeft="30px">
						<TableContainer
							component={Paper}
							style={{ boxShadow: 'none', marginTop: '30px' }}>
							<Table
								sx={{ minWidth: 700 }}
								aria-label="customized table">
								<TableHead>
									<TableRow>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Username
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Account
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Work email
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Account Created Platform
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Created on
											</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="tableHeader">
												Actions
											</Typography>
										</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{userList && userList.length > 0 ? (
										userList
											.slice(
												page * rowsPerPage,
												page * rowsPerPage +
													rowsPerPage,
											)
											.map((user, index) => {
												return (
													<StyledTableRow key={index}>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.username}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.account}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.email}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{user.accountType
																	? user.accountType
																	: 'N/A'}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															<Typography variant="tableBody">
																{new Date(
																	user.joinedDate,
																)
																	.getDate()
																	.toString()
																	.padStart(
																		2,
																		'0',
																	) +
																	'/' +
																	(
																		new Date(
																			user.joinedDate,
																		).getMonth() +
																		1
																	)
																		.toString()
																		.padStart(
																			2,
																			'0',
																		) +
																	'/' +
																	new Date(
																		user.joinedDate,
																	)
																		.getFullYear()
																		.toString()}
															</Typography>
														</StyledTableCell>
														<StyledTableCell align="center">
															{/* <IconBtn
																sx={{
																	marginRight:
																		'3px',
																}}
																onClick={() =>
																	onOpenUserControls(
																		user.id,
																		user.username,
																		user.userAccountVersion,
																		user.featuresTypes,
																		user.accountType,
																	)
																}>
																<EditIcon />
															</IconBtn> */}
															<AlertDialog
																id={user.id}
																userRoleId={
																	user.userRoleId
																}
																title={
																	'Remove User'
																}
																bodyContent={
																	'Removing user will permanently delete all the data of this user.'
																}
																icon={() => (
																	<DeleteIcon />
																)}
																deleteData={
																	removeUser
																}
																cancelBtnText={
																	'Keep the user'
																}
																deleteBtnText={
																	'Remove user'
																}
															/>
														</StyledTableCell>
													</StyledTableRow>
												);
											})
									) : (
										<StyledTableRow>
											<StyledTableCell
												colSpan={7}
												align="center">
												<Typography variant="tableBody">
													No data available
												</Typography>
											</StyledTableCell>
										</StyledTableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[]}
							component="div"
							count={userList && userList.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Grid>
				</Grid>
			</div>
			<UserControlPopup
				isModalOpen={isModalOpen}
				selectedUser={selectedUser}
				onClickBtn={updateUserDetails}
				onChangeUserType={onChangeUserType}
				onClose={onCloseModal}
				accTypeError={accountSelectionError}
			/>
			<PopupDialog
				title={'Add New User'}
				btnTitle={'Create'}
				isModalOpen={isUserModalOpen}
				onClose={onCloseUserModal}
				onClickBtn={onAddNewEndUser}>
				<Grid
					container
					size={{ xs: 12, md: 12 }}
					style={{
						textAlign: 'left',
						border: '1px solid #F4F5F6',
						boxShadow: '0px 10px 110px 1px rgba(59, 59, 59, 0.08)',
						marginTop: '20px',
						paddingTop: '20px',
						padding: '15px',
						borderRadius: '10px',
						backgroundColor: '#FFF',
					}}>
					<Grid container flexDirection={'column'} paddingY={'10px'}>
						<Typography
							fontSize={'16px'}
							width={'100%'}
							color={'#7C7C7C'}
							fontWeight={'400'}
							marginBottom={'5px'}>
							User Type
						</Typography>
						<Select
							size="small"
							fullWidth
							value={newEndUser.userType}
							error={newEndUserError.userType?.status}
							displayEmpty
							renderValue={value => {
								if (!value && value === '') {
									return (
										<Typography color="#cacdd4">
											Select User Type
										</Typography>
									);
								}
								return value;
							}}
							onChange={e => {
								onHandleInputChange(e.target.value, 'userType');
							}}
							sx={{
								backgroundColor: '#FFFFFF',
								color: '#777E90',
								marginTop: '5px',
							}}>
							{userAccountTypes.map((item, index) => {
								return (
									<MenuItem key={index} value={item.name}>
										{item.name}
									</MenuItem>
								);
							})}
						</Select>
						<Typography style={helperText}>
							{newEndUserError.userType.msg}
						</Typography>
					</Grid>
					<CustomTextInput
						label={'First Name'}
						onChange={value =>
							onHandleInputChange(value, 'firstName')
						}
						error={newEndUserError.firstName}
						width={'100%'}
						showRequiredMark={false}
					/>
					<CustomTextInput
						label={'Last Name'}
						onChange={value =>
							onHandleInputChange(value, 'lastName')
						}
						error={newEndUserError.lastName}
						width={'100%'}
						showRequiredMark={false}
					/>
					<CustomTextInput
						label={'Email'}
						onChange={value => onHandleInputChange(value, 'email')}
						error={newEndUserError.email}
						width={'100%'}
						showRequiredMark={false}
					/>
					<Typography
						fontSize={'16px'}
						color={'#7C7C7C'}
						paddingY="10px">
						Password
					</Typography>
					<Grid
						container
						justifyContent={'space-between'}
						alignItems={'center'}
						border={
							newEndUserError.password.status
								? '1px solid red'
								: '1px solid #D9D9D9'
						}
						color={'#777E90'}
						paddingX={'15px'}
						borderRadius={'5px'}>
						<InputBase
							type={show === false ? 'password' : 'text'}
							sx={{
								padding: '4px',
								width: '90%',
								color: '#777E90',
							}}
							error={newEndUserError.password.status}
							onChange={e =>
								onHandleInputChange(e.target.value, 'password')
							}
						/>
						<ButtonBase onClick={() => setShow(!show)}>
							{show ? (
								<HideEyeIcon
									error={newEndUserError.password.status}
								/>
							) : (
								<EyeIcon
									error={newEndUserError.password.status}
								/>
							)}
						</ButtonBase>
					</Grid>
					<Typography style={helperText}>
						{newEndUserError.password.msg}
					</Typography>
				</Grid>
			</PopupDialog>
		</>
	);
}

export const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		maxWidth: 80,
	},
}));

export const StyledTableRow = styled(TableRow)(() => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export const TableHeaderText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '600',
	color: 'rgba(0, 0, 0, 0.5)',
}));

export const TableRowText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '400',
	color: 'rgba(0, 0, 0, 1)',
}));

export default AdminDashboard;
